import React from 'react';
import { Stack, Text, CommandBar, ICommandBarItemProps } from '@fluentui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useHelp } from '../context/HelpContext';
import { HelpPanel } from './HelpPanel';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { helpMode, toggleHelpMode, openManual } = useHelp();

  const menuItems: ICommandBarItemProps[] = [
    {
      key: 'bestellungen',
      text: 'Bestellungen',
      iconProps: { iconName: 'DocumentSet' },
      onClick: () => navigate('/bestellungen'),
      className: location.pathname === '/bestellungen' ? 'is-selected' : '',
    },
    {
      key: 'archiv',
      text: 'Archiv',
      iconProps: { iconName: 'Archive' },
      onClick: () => navigate('/archiv'),
      className: location.pathname === '/archiv' ? 'is-selected' : '',
    },
    {
      key: 'artikel',
      text: 'Artikel',
      iconProps: { iconName: 'Product' },
      onClick: () => navigate('/artikel'),
      className: location.pathname === '/artikel' ? 'is-selected' : '',
    },
    {
      key: 'kreditoren',
      text: 'Kreditoren',
      iconProps: { iconName: 'ContactCard' },
      onClick: () => navigate('/kreditoren'),
      className: location.pathname === '/kreditoren' ? 'is-selected' : '',
    },
    {
      key: 'mengeneinheiten',
      text: 'Mengeneinheiten',
      iconProps: { iconName: 'NumberSymbol' },
      onClick: () => navigate('/mengeneinheiten'),
      className: location.pathname === '/mengeneinheiten' ? 'is-selected' : '',
    },
    {
      key: 'freigaben',
      text: 'Freigaben',
      iconProps: { iconName: 'CheckMark' },
      onClick: () => navigate('/freigaben'),
      className: location.pathname === '/freigaben' ? 'is-selected' : '',
    },
  ];

  const farItems: ICommandBarItemProps[] = [
    {
      key: 'help',
      ariaLabel: 'Hilfe',
      iconOnly: true,
      iconProps: { iconName: 'Unknown' },
      title: 'Hilfe',
      subMenuProps: {
        items: [
          {
            key: 'manual',
            text: 'Handbuch',
            iconProps: { iconName: 'TextDocument' },
            onClick: openManual,
          },
          { key: 'help-divider', itemType: 1 },
          {
            key: 'help-toggle',
            text: helpMode ? 'Hilfe-Tooltips: AN' : 'Hilfe-Tooltips: AUS',
            iconProps: { iconName: helpMode ? 'ToggleRight' : 'ToggleLeft' },
            onClick: toggleHelpMode,
            style: helpMode ? { color: '#107c10', fontWeight: 600 } : undefined,
          },
        ],
      },
    },
    {
      key: 'settings',
      text: 'Einstellungen',
      iconProps: { iconName: 'Settings' },
      onClick: () => navigate('/einstellungen'),
    },
    {
      key: 'user',
      text: user?.displayName || 'User',
      iconProps: { iconName: 'Contact' },
      subMenuProps: {
        items: [
          {
            key: 'profile',
            text: user?.displayName || '',
            secondaryText: user?.email || '',
            iconProps: { iconName: 'Contact' },
          },
          {
            key: 'divider',
            itemType: 1, // Divider
          },
          {
            key: 'logout',
            text: 'Abmelden',
            iconProps: { iconName: 'SignOut' },
            onClick: () => {
              logout();
            },
          },
        ],
      },
    },
  ];

  return (
    <Stack styles={{ root: { height: '100vh', width: '100%' } }}>
      {/* Header mit Titel */}
      <Stack
        horizontal
        verticalAlign="center"
        styles={{
          root: {
            backgroundColor: '#0078d4',
            padding: '12px 24px',
            color: 'white',
            height: 48,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Text
          variant="xLarge"
          styles={{
            root: {
              fontWeight: 600,
              color: 'white',
              marginRight: '32px',
            },
          }}
        >
          Bestellwesen
        </Text>
      </Stack>

      {/* Command Bar (horizontale Navigation) */}
      <CommandBar
        items={menuItems}
        farItems={farItems}
        styles={{
          root: {
            padding: 0,
            backgroundColor: '#f3f2f1',
            borderBottom: '1px solid #edebe9',
          },
        }}
      />

      {/* Main Content Area */}
      <Stack.Item
        grow
        styles={{
          root: {
            overflow: 'auto',
            padding: '24px 32px',
            backgroundColor: '#faf9f8',
          },
        }}
      >
        {children}
      </Stack.Item>

      {/* Hilfe-Panel (Handbuch) */}
      <HelpPanel />
    </Stack>
  );
};
